import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';
import './BkashPaymentPage.css';

const BKASH_NUMBER = process.env.REACT_APP_BKASH_NUMBER || '01622817880';
const PAYMENT_WINDOW_MINUTES = 30;

export default function BkashPaymentPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const orderId     = state?.orderId;
  const orderNumber = state?.orderNumber;
  const amount      = state?.amount;

  const [secondsLeft, setSecondsLeft] = useState(PAYMENT_WINDOW_MINUTES * 60);
  const [form, setForm]       = useState({ senderBkashNumber: '', trxId: '', customerEmail: '' });
  const [errors, setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [copyDone, setCopyDone]     = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!orderId) { navigate('/'); return; }

    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [orderId, navigate]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const seconds = String(secondsLeft % 60).padStart(2, '0');
  const expired = secondsLeft === 0;

  const copyNumber = () => {
    navigator.clipboard.writeText(BKASH_NUMBER);
    setCopyDone(true);
    setTimeout(() => setCopyDone(false), 2000);
  };

  const validate = () => {
    const e = {};
    const phone = form.senderBkashNumber.replace(/\D/g, '');
    if (!phone) e.senderBkashNumber = 'bKash number is required';
    else if (phone.length !== 11) e.senderBkashNumber = 'Must be exactly 11 digits';

    const trx = form.trxId.trim().toUpperCase();
    if (!trx) e.trxId = 'Transaction ID is required';
    else if (!/^[A-Z0-9]{10}$/.test(trx)) e.trxId = 'Must be exactly 10 alphanumeric characters';

    if (form.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail)) {
      e.customerEmail = 'Invalid email address';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/bkash/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId,
          senderBkashNumber: form.senderBkashNumber.replace(/\D/g, ''),
          trxId: form.trxId.trim().toUpperCase(),
          customerEmail: form.customerEmail || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        clearInterval(timerRef.current);
      } else {
        setErrors({ submit: data.message || 'Submission failed. Please try again.' });
      }
    } catch {
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Confirmation screen ───────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="bkash-page">
        <div className="bkash-card bkash-success-card">
          <div className="bkash-success-icon">✓</div>
          <h2>Payment Submitted!</h2>
          <p>We've received your payment details for order <strong>{orderNumber}</strong>.</p>
          <p>Our team will verify your bKash transaction and confirm your order shortly.</p>
          <p className="bkash-success-note">
            If you provided an email, you'll receive a confirmation once verified.
          </p>
          <button className="bkash-home-btn" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Expired screen ────────────────────────────────────────────────────────
  if (expired) {
    return (
      <div className="bkash-page">
        <div className="bkash-card bkash-expired-card">
          <div className="bkash-expired-icon">⏰</div>
          <h2>Payment Window Expired</h2>
          <p>The 30-minute payment window for order <strong>{orderNumber}</strong> has closed.</p>
          <p>Please contact us if you've already sent the payment.</p>
          <button className="bkash-home-btn" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── Main payment page ─────────────────────────────────────────────────────
  return (
    <div className="bkash-page">
      <div className="bkash-card">
        {/* Header */}
        <div className="bkash-header">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Bkash_Bangladesh.svg/320px-Bkash_Bangladesh.svg.png"
            alt="bKash"
            className="bkash-logo"
          />
          <h2>Complete Your Payment</h2>
          <p className="bkash-order-ref">Order: <strong>{orderNumber}</strong></p>
        </div>

        {/* Countdown */}
        <div className={`bkash-timer ${secondsLeft < 300 ? 'bkash-timer--urgent' : ''}`}>
          <span className="bkash-timer-label">Time remaining</span>
          <span className="bkash-timer-value">{minutes}:{seconds}</span>
        </div>

        {/* Instructions */}
        <div className="bkash-instructions">
          <h3>How to pay</h3>
          <ol>
            <li>Open your <strong>bKash app</strong> or dial <strong>*247#</strong></li>
            <li>Select <strong>Send Money</strong></li>
            <li>Enter the number below and send exactly <strong>৳{amount?.toFixed(2)}</strong></li>
            <li>Copy your <strong>Transaction ID (TrxID)</strong> from the confirmation SMS</li>
            <li>Fill in the form below and submit</li>
          </ol>
        </div>

        {/* bKash number + amount */}
        <div className="bkash-target">
          <div className="bkash-target-row">
            <div className="bkash-target-block">
              <span className="bkash-target-label">Send to</span>
              <span className="bkash-target-number">{BKASH_NUMBER}</span>
              <button className="bkash-copy-btn" onClick={copyNumber}>
                {copyDone ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <div className="bkash-target-block">
              <span className="bkash-target-label">Exact amount</span>
              <span className="bkash-target-amount">৳{amount?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Submission form */}
        <form className="bkash-form" onSubmit={handleSubmit}>
          <div className="bkash-form-group">
            <label>Your bKash Number *</label>
            <input
              type="tel"
              name="senderBkashNumber"
              value={form.senderBkashNumber}
              onChange={handleChange}
              placeholder="01XXXXXXXXX"
              maxLength={11}
              className={errors.senderBkashNumber ? 'input-error' : ''}
            />
            {errors.senderBkashNumber && <span className="bkash-error">{errors.senderBkashNumber}</span>}
          </div>

          <div className="bkash-form-group">
            <label>Transaction ID (TrxID) *</label>
            <input
              type="text"
              name="trxId"
              value={form.trxId}
              onChange={handleChange}
              placeholder="e.g. AB1CD2EF3G"
              maxLength={10}
              className={errors.trxId ? 'input-error' : ''}
              style={{ textTransform: 'uppercase' }}
            />
            <span className="bkash-hint">10-character code from your bKash SMS</span>
            {errors.trxId && <span className="bkash-error">{errors.trxId}</span>}
          </div>

          <div className="bkash-form-group">
            <label>Email for confirmation <span className="bkash-optional">(optional)</span></label>
            <input
              type="email"
              name="customerEmail"
              value={form.customerEmail}
              onChange={handleChange}
              placeholder="you@example.com"
              className={errors.customerEmail ? 'input-error' : ''}
            />
            {errors.customerEmail && <span className="bkash-error">{errors.customerEmail}</span>}
          </div>

          <div className="bkash-form-group bkash-order-id-field">
            <label>Order ID</label>
            <input type="text" value={orderNumber} readOnly />
          </div>

          {errors.submit && <p className="bkash-submit-error">{errors.submit}</p>}

          <button type="submit" className="bkash-submit-btn" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Payment'}
          </button>
        </form>
      </div>
    </div>
  );
}
